import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CurrencyGbp, ShieldCheck, Gear, FloppyDisk, Buildings, Users } from "@phosphor-icons/react";
import { toast } from "sonner";

const SECTORS = [
  "Healthcare & Social Care", "IT & Technology", "Accounting & Finance",
  "Education & Childcare", "Commercial Sales & Business Development",
  "Sales, Revenue & Customer Success", "All Sectors",
];

const EMPTY_3VAC = {
  active: true, sector: "All Sectors", annual_price: "", monthly_price: "", included_placements: 3,
  salary_band_min: 20000, salary_band_max: 60000, payment_frequency: "monthly",
  fair_use_max_active_vacancies: 3, fair_use_max_submissions_per_vacancy: 10,
  terms_notes: "", replacement_guarantee_days: 14,
};

const EMPTY_UNLIMITED = {
  active: true, sector: "All Sectors", monthly_price: "", annual_equivalent: "",
  salary_ceiling: 60000, fair_use_max_concurrent_vacancies: 10,
  fair_use_max_new_per_month: 5, fair_use_max_submissions_per_vacancy: 10,
  exclusions: "", additional_fee_above_ceiling: "negotiable",
  approval_required: false, terms_notes: "",
};

function NumberInput({ label, value, onChange, prefix, ...props }) {
  return (
    <div>
      <label className="text-xs font-medium text-[#374151] dark:text-[#CBD5E1] block mb-1">{label}</label>
      <div className="flex items-center">
        {prefix && <span className="text-sm text-[#6B7280] mr-1">{prefix}</span>}
        <Input type="number" value={value} onChange={e => onChange(e.target.value)} className="h-9 text-sm" {...props} />
      </div>
    </div>
  );
}

function TextInput({ label, value, onChange, ...props }) {
  return (
    <div>
      <label className="text-xs font-medium text-[#374151] dark:text-[#CBD5E1] block mb-1">{label}</label>
      <Input value={value} onChange={e => onChange(e.target.value)} className="h-9 text-sm" {...props} />
    </div>
  );
}

export default function PartnershipPricingPage() {
  const [threeVac, setThreeVac] = useState(EMPTY_3VAC);
  const [unlimited, setUnlimited] = useState(EMPTY_UNLIMITED);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/mr-banks/partnership-config").then(({ data }) => {
      if (data.three_vacancy) setThreeVac(v => ({ ...v, ...data.three_vacancy }));
      if (data.unlimited) setUnlimited(v => ({ ...v, ...data.unlimited }));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/mr-banks/partnership-config", { three_vacancy: threeVac, unlimited });
      toast.success("Partnership pricing saved");
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[#0F172A] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="partnership-pricing-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#0F172A] dark:text-white flex items-center gap-2">
            <CurrencyGbp size={24} weight="duotone" className="text-[#C5A059]" />
            Partnership Pricing
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">Configure recruitment partnership commercial terms for Mr Banks</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#0F172A] hover:bg-[#1E293B] gap-1.5" data-testid="save-pricing-btn">
          <FloppyDisk size={16} /> {saving ? "Saving..." : "Save Configuration"}
        </Button>
      </div>

      {/* Standard Terms Reference */}
      <div className="bg-[#F9FAFB] dark:bg-[#0F172A] rounded-lg border border-[#E5E7EB] dark:border-[#334155] p-4">
        <h3 className="text-sm font-semibold text-[#0F172A] dark:text-white flex items-center gap-2 mb-2"><ShieldCheck size={16} className="text-[#C5A059]" /> Standard Commercial Terms (Fixed)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-[#6B7280]">
          <div><div className="font-medium text-[#0F172A] dark:text-white">Standard Fee</div>15% of first-year annual salary</div>
          <div><div className="font-medium text-[#0F172A] dark:text-white">Guarantee</div>14-day replacement guarantee</div>
          <div><div className="font-medium text-[#0F172A] dark:text-white">3-Month Plan</div>30% / 35% / 35%</div>
          <div><div className="font-medium text-[#0F172A] dark:text-white">6-Month Plan</div>25% + 15% × 5 months</div>
        </div>
      </div>

      {/* 3-Vacancy Partnership */}
      <div className="bg-white dark:bg-[#1E293B] rounded-lg border border-[#E5E7EB] dark:border-[#334155] p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-[#0F172A] dark:text-white flex items-center gap-2">
            <Buildings size={18} className="text-[#C5A059]" /> 3-Placement Recruitment Partnership
          </h2>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#6B7280]">{threeVac.active ? "Active" : "Inactive"}</span>
            <Switch checked={threeVac.active} onCheckedChange={v => setThreeVac(f => ({ ...f, active: v }))} data-testid="3vac-active" />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <NumberInput label="Annual Price (£)" value={threeVac.annual_price} onChange={v => setThreeVac(f => ({ ...f, annual_price: v }))} prefix="£" data-testid="3vac-annual" />
          <NumberInput label="Monthly Price (£)" value={threeVac.monthly_price} onChange={v => setThreeVac(f => ({ ...f, monthly_price: v }))} prefix="£" data-testid="3vac-monthly" />
          <NumberInput label="Included Placements" value={threeVac.included_placements} onChange={v => setThreeVac(f => ({ ...f, included_placements: parseInt(v) || 0 }))} />
          <NumberInput label="Min Salary (£)" value={threeVac.salary_band_min} onChange={v => setThreeVac(f => ({ ...f, salary_band_min: parseInt(v) || 0 }))} prefix="£" />
          <NumberInput label="Max Salary (£)" value={threeVac.salary_band_max} onChange={v => setThreeVac(f => ({ ...f, salary_band_max: parseInt(v) || 0 }))} prefix="£" />
          <NumberInput label="Guarantee (days)" value={threeVac.replacement_guarantee_days} onChange={v => setThreeVac(f => ({ ...f, replacement_guarantee_days: parseInt(v) || 0 }))} />
          <div>
            <label className="text-xs font-medium text-[#374151] dark:text-[#CBD5E1] block mb-1">Sector</label>
            <Select value={threeVac.sector} onValueChange={v => setThreeVac(f => ({ ...f, sector: v }))}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-[#374151] dark:text-[#CBD5E1] block mb-1">Payment Frequency</label>
            <Select value={threeVac.payment_frequency} onValueChange={v => setThreeVac(f => ({ ...f, payment_frequency: v }))}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
                <SelectItem value="annually">Annually</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <NumberInput label="Fair Use: Max Active Vacancies" value={threeVac.fair_use_max_active_vacancies} onChange={v => setThreeVac(f => ({ ...f, fair_use_max_active_vacancies: parseInt(v) || 0 }))} />
        </div>
        <div className="mt-3">
          <label className="text-xs font-medium text-[#374151] dark:text-[#CBD5E1] block mb-1">Terms / Notes</label>
          <textarea value={threeVac.terms_notes} onChange={e => setThreeVac(f => ({ ...f, terms_notes: e.target.value }))} className="w-full rounded-md border border-[#E5E7EB] dark:border-[#334155] dark:bg-[#0F172A] px-3 py-2 text-sm min-h-[60px] resize-none" placeholder="Additional terms or notes for agents..." />
        </div>
      </div>

      {/* Unlimited Partnership */}
      <div className="bg-white dark:bg-[#1E293B] rounded-lg border border-[#E5E7EB] dark:border-[#334155] p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-[#0F172A] dark:text-white flex items-center gap-2">
            <Users size={18} className="text-[#C5A059]" /> Unlimited Recruitment Partnership
          </h2>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#6B7280]">{unlimited.active ? "Active" : "Inactive"}</span>
            <Switch checked={unlimited.active} onCheckedChange={v => setUnlimited(f => ({ ...f, active: v }))} data-testid="unlimited-active" />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <NumberInput label="Monthly Price (£)" value={unlimited.monthly_price} onChange={v => setUnlimited(f => ({ ...f, monthly_price: v }))} prefix="£" data-testid="unlimited-monthly" />
          <NumberInput label="Annual Equivalent (£)" value={unlimited.annual_equivalent} onChange={v => setUnlimited(f => ({ ...f, annual_equivalent: v }))} prefix="£" />
          <NumberInput label="Salary Ceiling (£)" value={unlimited.salary_ceiling} onChange={v => setUnlimited(f => ({ ...f, salary_ceiling: parseInt(v) || 0 }))} prefix="£" data-testid="unlimited-ceiling" />
          <div>
            <label className="text-xs font-medium text-[#374151] dark:text-[#CBD5E1] block mb-1">Sector</label>
            <Select value={unlimited.sector} onValueChange={v => setUnlimited(f => ({ ...f, sector: v }))}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <NumberInput label="Max Concurrent Vacancies" value={unlimited.fair_use_max_concurrent_vacancies} onChange={v => setUnlimited(f => ({ ...f, fair_use_max_concurrent_vacancies: parseInt(v) || 0 }))} />
          <NumberInput label="Max New Vacancies/Month" value={unlimited.fair_use_max_new_per_month} onChange={v => setUnlimited(f => ({ ...f, fair_use_max_new_per_month: parseInt(v) || 0 }))} />
          <NumberInput label="Max Submissions/Vacancy" value={unlimited.fair_use_max_submissions_per_vacancy} onChange={v => setUnlimited(f => ({ ...f, fair_use_max_submissions_per_vacancy: parseInt(v) || 0 }))} />
          <TextInput label="Additional Fee Above Ceiling" value={unlimited.additional_fee_above_ceiling} onChange={v => setUnlimited(f => ({ ...f, additional_fee_above_ceiling: v }))} placeholder="e.g. negotiable, 15%" />
          <div className="flex items-center gap-2 self-end pb-1">
            <Switch checked={unlimited.approval_required} onCheckedChange={v => setUnlimited(f => ({ ...f, approval_required: v }))} />
            <span className="text-xs text-[#6B7280]">Requires management approval</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-3">
          <div>
            <label className="text-xs font-medium text-[#374151] dark:text-[#CBD5E1] block mb-1">Exclusions</label>
            <textarea value={unlimited.exclusions} onChange={e => setUnlimited(f => ({ ...f, exclusions: e.target.value }))} className="w-full rounded-md border border-[#E5E7EB] dark:border-[#334155] dark:bg-[#0F172A] px-3 py-2 text-sm min-h-[50px] resize-none" placeholder="Roles or sectors excluded..." />
          </div>
          <div>
            <label className="text-xs font-medium text-[#374151] dark:text-[#CBD5E1] block mb-1">Terms / Notes</label>
            <textarea value={unlimited.terms_notes} onChange={e => setUnlimited(f => ({ ...f, terms_notes: e.target.value }))} className="w-full rounded-md border border-[#E5E7EB] dark:border-[#334155] dark:bg-[#0F172A] px-3 py-2 text-sm min-h-[50px] resize-none" placeholder="Additional terms..." />
          </div>
        </div>
      </div>

      <div className="text-[10px] text-[#9CA3AF]">
        Mr Banks will use these configured values during live calls. Agents cannot edit pricing. If a client request falls outside configured limits, Mr Banks will recommend "Management approval required" rather than inventing a price.
      </div>
    </div>
  );
}
